import React, { useState, useEffect } from 'react';
import styles from './InvoiceModal.module.css';

const InvoiceModal = ({ isOpen, onClose, onSave }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoiceData, setInvoiceData] = useState({
    customerId: '',
    customerName: '',
    amount: '',
    description: '',
    dueDate: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchContacts();
    }
  }, [isOpen]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      // First fetch the CRM index file to get the structure
      const indexResponse = await fetch('https://kindred-cloudflare.shepherdn.workers.dev/api/crm/index');
      if (!indexResponse.ok) {
        throw new Error(`Failed to fetch CRM index: ${indexResponse.status}`);
      }
      const indexData = await indexResponse.json();
      
      // Then fetch all contacts
      const contactsResponse = await fetch('https://kindred-cloudflare.shepherdn.workers.dev/api/contacts');
      if (!contactsResponse.ok) {
        throw new Error(`Failed to fetch contacts: ${contactsResponse.status}`);
      }
      const contactsData = await contactsResponse.json();
      
      setContacts(contactsData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError(`Failed to load contacts: ${err.message}`);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInvoiceData({ ...invoiceData, [name]: value });
  };

  const handleSelectContact = (e) => {
    const selectedId = e.target.value;
    const selectedContact = contacts.find(contact => contact.id === selectedId);
    
    if (selectedContact) {
      setInvoiceData({
        ...invoiceData,
        customerId: selectedContact.id,
        customerName: selectedContact.name || `${selectedContact.firstName} ${selectedContact.lastName}`.trim()
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!invoiceData.customerId || !invoiceData.amount) {
      setError('Please select a customer and enter an amount');
      return;
    }
    
    // Convert amount to cents for consistency with existing code
    const amountInCents = Math.round(parseFloat(invoiceData.amount) * 100);
    
    const newInvoice = {
      customer: invoiceData.customerName,
      customerId: invoiceData.customerId,
      amount: amountInCents,
      description: invoiceData.description,
      status: 'Pending',
      date: new Date().toISOString(),
      dueDate: invoiceData.dueDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // Default to 30 days
    };
    
    onSave(newInvoice);
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2>Generate New Invoice</h2>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>
        
        {error && (
          <div className={styles.errorMessage}>
            {error}
            <button onClick={() => setError(null)} className={styles.dismissButton}>Dismiss</button>
          </div>
        )}
        
        {loading ? (
          <div className={styles.loadingIndicator}>Loading contacts...</div>
        ) : (
          <form onSubmit={handleSubmit} className={styles.invoiceForm}>
            <div className={styles.formGroup}>
              <label htmlFor="customer">Customer</label>
              <select 
                id="customer" 
                name="customer" 
                value={invoiceData.customerId} 
                onChange={handleSelectContact}
                className={styles.formControl}
                required
              >
                <option value="">-- Select Customer --</option>
                {contacts.map(contact => (
                  <option key={contact.id} value={contact.id}>
                    {contact.name || `${contact.firstName} ${contact.lastName}`.trim()}
                  </option>
                ))}
              </select>
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="amount">Amount ($)</label>
              <input 
                type="number" 
                id="amount" 
                name="amount" 
                value={invoiceData.amount} 
                onChange={handleInputChange}
                className={styles.formControl}
                min="0.01" 
                step="0.01"
                required
                placeholder="0.00"
              />
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="description">Description</label>
              <textarea 
                id="description" 
                name="description" 
                value={invoiceData.description} 
                onChange={handleInputChange}
                className={styles.formControl}
                rows="3"
                placeholder="Invoice description..."
              />
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="dueDate">Due Date</label>
              <input 
                type="date" 
                id="dueDate" 
                name="dueDate" 
                value={invoiceData.dueDate} 
                onChange={handleInputChange}
                className={styles.formControl}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            
            <div className={styles.formActions}>
              <button type="button" className={styles.cancelButton} onClick={onClose}>Cancel</button>
              <button type="submit" className={styles.saveButton}>Create Invoice</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default InvoiceModal;